import { ThemeOptions } from '@mui/material';

/**
 * Tema MUI de VisiónDERRED.
 *
 * Replica en MUI los tokens de `css/tokens.css` (lineamientos gráficos de UAI / CENTRA).
 * Si cambia un color acá, debe cambiar también allá.
 */

const BRAND = {
  cyan: '#00b0d9',
  cyanDark: '#00708d',
  cyanDeep: '#00657f',
  cyanTint: '#e3f5fa',
  ink: '#0b0e10',
  ink2: '#0e1519',
  text: '#22313a',
  mute: '#5e6e75',
  fieldBorder: '#7f9199',
  line: '#dce6ea',
  soft: '#f1f6f8'
};

const FONT_DISPLAY = "'Archivo', 'Source Sans 3', system-ui, -apple-system, 'Segoe UI', sans-serif";
const FONT_BODY = "'Source Sans 3', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Consolas, monospace";

// MUI pide 25 niveles de sombra. La marca casi no usa sombras: se dejan muy suaves y solo
// se notan en superficies flotantes (menús, diálogos, popovers).
const SOFT_SHADOW = '0 1px 2px rgba(11, 14, 16, 0.06)';
const OVERLAY_SHADOW = '0 18px 40px rgba(11, 14, 16, 0.18), 0 2px 6px rgba(11, 14, 16, 0.08)';
const shadows = [
  'none',
  SOFT_SHADOW,
  SOFT_SHADOW,
  ...Array(22).fill(OVERLAY_SHADOW)
] as ThemeOptions['shadows'];

export const vdrThemeOptions: ThemeOptions = {
  palette: {
    // El primario es el cian oscuro: es el que pasa contraste AA como texto sobre blanco.
    // Los botones "contained" usan el cian de marca con texto tinta (ver MuiButton).
    primary: {
      main: BRAND.cyanDark,
      dark: BRAND.cyanDeep,
      light: BRAND.cyan,
      contrastText: '#ffffff'
    },
    secondary: {
      main: BRAND.ink,
      dark: '#000000',
      light: BRAND.ink2,
      contrastText: '#ffffff'
    },
    error: { main: '#c62828' },
    warning: { main: '#b45309' },
    success: { main: '#1b7f4b' },
    info: { main: BRAND.cyanDark },
    text: {
      primary: BRAND.ink,
      secondary: BRAND.mute
    },
    divider: BRAND.line,
    background: {
      default: '#ffffff',
      paper: '#ffffff'
    },
    action: {
      hover: 'rgba(0, 176, 217, 0.08)',
      selected: 'rgba(0, 176, 217, 0.14)'
    }
  },
  shape: {
    borderRadius: 0
  },
  shadows,
  typography: {
    fontFamily: FONT_BODY,
    h1: { fontFamily: FONT_DISPLAY, fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontFamily: FONT_DISPLAY, fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontFamily: FONT_DISPLAY, fontWeight: 800, letterSpacing: '-0.01em' },
    h4: { fontFamily: FONT_DISPLAY, fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
    h6: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontFamily: FONT_DISPLAY, fontWeight: 700, textTransform: 'none', letterSpacing: 0 },
    overline: { fontFamily: FONT_MONO, fontWeight: 500, letterSpacing: '0.12em' },
    caption: { color: BRAND.mute }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { fontFamily: FONT_BODY }
      }
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': { outline: `2px solid ${BRAND.cyanDark}`, outlineOffset: 2 }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
        },
        containedPrimary: {
          backgroundColor: BRAND.cyan,
          color: BRAND.ink,
          '&:hover': { backgroundColor: BRAND.ink, color: '#ffffff' }
        },
        outlinedPrimary: {
          borderColor: BRAND.cyanDark,
          color: BRAND.cyanDark,
          '&:hover': { borderColor: BRAND.ink, color: BRAND.ink, backgroundColor: 'transparent' }
        },
        textPrimary: {
          color: BRAND.cyanDark,
          '&:hover': { color: BRAND.ink, backgroundColor: 'rgba(0, 176, 217, 0.08)' }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 0 }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: BRAND.line }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: { border: `1px solid ${BRAND.line}`, boxShadow: 'none' }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 0, borderTop: `4px solid ${BRAND.cyan}` }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: FONT_DISPLAY, fontWeight: 800 }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: { border: `1px solid ${BRAND.line}` }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': { backgroundColor: BRAND.cyanTint },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 176, 217, 0.2)' }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: BRAND.ink,
          color: '#ffffff',
          borderRadius: 0,
          fontFamily: FONT_BODY,
          fontSize: 13,
          padding: '6px 10px'
        },
        arrow: { color: BRAND.ink }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.mute },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.cyanDark }
        },
        notchedOutline: { borderColor: BRAND.fieldBorder }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': { color: BRAND.mute, opacity: 1 }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 0, fontWeight: 600 }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: BRAND.cyan, height: 3 }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT_DISPLAY,
          fontWeight: 600,
          textTransform: 'none',
          '&.Mui-selected': { color: BRAND.ink }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: FONT_MONO,
          fontWeight: 500,
          fontSize: 11,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: BRAND.mute,
          borderBottom: `2px solid ${BRAND.ink}`
        },
        body: {
          color: BRAND.text,
          borderBottom: `1px solid ${BRAND.line}`
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: { borderRadius: 0 },
        rail: { borderRadius: 0 },
        track: { borderRadius: 0 },
        thumb: { borderRadius: 0 },
        valueLabel: { borderRadius: 0, backgroundColor: BRAND.ink, fontFamily: FONT_MONO, fontSize: 12 }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 0 }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: BRAND.cyanTint },
        bar: { backgroundColor: BRAND.cyan }
      }
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: { color: BRAND.cyan }
      }
    }
  }
};
